/**
 * Runtime map of approved footer logo keys → inline SVG components for artifact footers.
 * Keep allowlist keys in sync with CMT `marketingMessageFooterLogoAllowlist.tsx`.
 * SVG markup may be updated independently of keys.
 */
import type { ComponentType, SVGProps } from 'react';
import JPMorganLogo from '@/enterprise/assets/JPMorganLogo';
import MonduJPMorganLogo from '@/enterprise/assets/MonduJPMorganLogo';
import MonduLogo from '@/enterprise/assets/MonduLogo';
import type { MarketingMessageArtifactContent } from '@/enterprise/data/useArtifacts';

/** The footer logo keys SEA can actually send, straight from the generated contract. */
type ApiFooterLogoKey = NonNullable<NonNullable<MarketingMessageArtifactContent['footer']>['logo']['iconKey']>;

/**
 * `satisfies` ties this list to the contract: a key SEA drops, or one misspelled here, is a compile
 * error rather than a logo that silently resolves to nothing at runtime. It stays a literal array
 * because the registry needs the keys at runtime to build `MARKETING_MESSAGE_FOOTER_LOGO_REGISTRY`,
 * and a type union gives no runtime values.
 */
export const MARKETING_MESSAGE_FOOTER_LOGO_ALLOWLIST = [
  'FundboxLogo',
  'MonduLogo',
  'JPMorganLogo',
  'MonduJPMorganLogo',
] as const satisfies readonly ApiFooterLogoKey[];

export type MarketingMessageFooterLogoKey = (typeof MARKETING_MESSAGE_FOOTER_LOGO_ALLOWLIST)[number];

type FooterLogoComponent = ComponentType<SVGProps<SVGSVGElement>>;

const FUNDBOX_FOOTER_LOGO_CLIP_ID = 'fundbox-footer-logo-clip';

const FundboxLogo = (props: SVGProps<SVGSVGElement>): React.ReactElement => (
  <svg viewBox="0 0 97 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath={`url(#${FUNDBOX_FOOTER_LOGO_CLIP_ID})`}>
      <path
        d="M96.5851 5.25446L92.3679 10.9939L97 17.2693H94.4877L91.1233 12.6912L87.7813 17.2693H85.3846L89.9481 11.0832L85.6614 5.25446H88.173L91.1919 9.38592L94.2347 5.25446H96.5851Z"
        fill="currentColor"
      />
      <path
        d="M76.8113 11.3525C76.8113 14.5649 78.4745 15.8141 80.4615 15.8141C82.4485 15.8141 84.1117 14.4311 84.1117 11.3525C84.1117 8.1402 82.4485 6.89096 80.4615 6.89096C78.4745 6.89096 76.8113 8.0733 76.8113 11.3525ZM74.5703 11.3525C74.5703 7.0025 77.4814 5.10632 80.4615 5.10632C83.4416 5.10632 86.3531 7.0025 86.3531 11.3525C86.3531 15.7026 83.4416 17.5988 80.4615 17.5988C77.4814 17.5988 74.5703 15.7026 74.5703 11.3525Z"
        fill="currentColor"
      />
      <path
        d="M64.6443 11.3289C64.6443 14.5086 66.4074 15.8522 68.2864 15.8522C70.5367 15.8522 71.7894 14.2623 71.7894 11.3289C71.7894 8.3955 70.5367 6.80561 68.2864 6.80561C66.4074 6.80561 64.6443 8.1492 64.6443 11.3289ZM62.626 0.401367H64.7371V7.1639C65.5027 5.88752 66.8017 5.05901 68.82 5.05901C71.7662 5.05901 74.086 7.38782 74.086 11.3289C74.086 15.3371 71.7662 17.5988 68.82 17.5988C66.8017 17.5988 65.5027 16.7703 64.7371 15.4939V17.3077H62.626V0.401367Z"
        fill="currentColor"
      />
      <path
        d="M59.2238 7.1639V0.401367H61.3348V17.3077H59.2238V15.4939C58.4583 16.7702 57.1591 17.5988 55.1409 17.5988C52.1947 17.5988 49.8749 15.3371 49.8749 11.3289C49.8749 7.38782 52.1947 5.059 55.1409 5.059C57.1591 5.059 58.4583 5.88752 59.2238 7.1639ZM59.3166 11.3289C59.3166 8.14913 57.5535 6.80561 55.6744 6.80561C53.4242 6.80561 52.1715 8.3955 52.1715 11.3289C52.1715 14.2623 53.4242 15.8522 55.6744 15.8522C57.5535 15.8522 59.3166 14.5086 59.3166 11.3289Z"
        fill="currentColor"
      />
      <path
        d="M40.9594 5.26856V7.10837C41.6613 5.97388 42.8386 5.10632 44.899 5.10632C47.8423 5.10632 48.9064 6.9749 48.9064 9.711V17.2743H46.8461V10.3116C46.8461 8.46533 46.5291 6.86367 44.1745 6.86367C42.1821 6.86367 40.9594 8.17613 40.9594 10.7788V17.2743H38.8992L38.8459 5.26856H40.9594Z"
        fill="currentColor"
      />
      <path
        d="M35.6953 17.2743V15.5848C34.9887 16.726 33.872 17.5987 31.8435 17.5987C29.0174 17.5987 27.9234 15.719 27.9234 12.9665V5.26855H29.9974V12.3623C29.9974 14.2197 30.2937 15.8309 32.5728 15.8309C34.5557 15.8309 35.6953 14.5106 35.6953 11.8924V5.26855H37.7693V17.2743H35.6953Z"
        fill="currentColor"
      />
      <path
        d="M22.1858 5.25094V4.04414C22.1858 1.83166 22.9507 0.401367 25.3803 0.401367C25.8527 0.401367 26.5275 0.423717 26.9549 0.513109V2.18923C26.4376 2.12219 26.0101 2.09984 25.6727 2.09984C24.5254 2.09984 24.233 2.63619 24.233 4.04414V5.25094H26.9549V6.88236H24.233V17.2743H22.1858V6.88236H20.4986V5.25094H22.1858Z"
        fill="currentColor"
      />
      <path d="M0 5.22217H4.79612V17.2743H0V5.22217Z" fill="currentColor" />
      <path
        d="M4.79626 0.401367L4.79626 5.2222L16.7866 5.2222L16.7866 0.401368L4.79626 0.401367Z"
        fill="currentColor"
      />
      <path d="M8.28403 8.72827H13.0801V13.5491H8.28403V8.72827Z" fill="currentColor" />
    </g>
    <defs>
      <clipPath id={FUNDBOX_FOOTER_LOGO_CLIP_ID}>
        <rect width="97" height="18" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/** Maps allowlisted footer logo keys to inline SVG components for artifact rendering. */
export const MARKETING_MESSAGE_FOOTER_LOGO_REGISTRY: Record<MarketingMessageFooterLogoKey, FooterLogoComponent> = {
  FundboxLogo,
  MonduLogo,
  JPMorganLogo,
  MonduJPMorganLogo,
};

export function isMarketingMessageFooterLogoKey(iconKey: string): iconKey is MarketingMessageFooterLogoKey {
  return Object.hasOwn(MARKETING_MESSAGE_FOOTER_LOGO_REGISTRY, iconKey);
}

export function resolveMarketingMessageFooterLogo(iconKey: string | undefined): FooterLogoComponent | null {
  if (iconKey && isMarketingMessageFooterLogoKey(iconKey)) {
    return MARKETING_MESSAGE_FOOTER_LOGO_REGISTRY[iconKey];
  }

  return null;
}
